import { ref, computed } from 'vue';

// 站点配置
const SITES = {
  aws: {
    id: 'aws',
    name: 'AWS Manager',
    url: 'ec2-manager.internal.robotruck.jp',
    icon: 'fab fa-aws',
    protocol: 'https://',
    provider: 'aws'
  },
  gcp: {
    id: 'gcp',
    name: 'GCP Manager', 
    url: 'gce-manager.internal.robotruck.jp',
    icon: 'fab fa-google',
    protocol: 'https://',
    provider: 'gcp'
  }
};

// 全局状态
const currentSiteId = ref(null);
const isSwitching = ref(false);

export function useSiteSwitcher() {
  
  // 检测当前站点
  const detectCurrentSite = () => {
    const hostname = window.location.hostname;
    
    if (hostname.includes('ec2-manager')) {
      return 'aws';
    } else if (hostname.includes('gce-manager')) {
      return 'gcp';
    }
    
    // 默认返回AWS（可以根据需要调整）
    return 'aws';
  };

  // 初始化当前站点
  if (!currentSiteId.value) {
    currentSiteId.value = detectCurrentSite();
  }

  // 当前站点信息
  const currentSite = computed(() => SITES[currentSiteId.value]);

  // 可用站点列表（排除当前站点）
  const availableSites = computed(() => {
    return Object.values(SITES).filter(site => site.id !== currentSiteId.value);
  });

  // 所有站点列表
  const allSites = computed(() => Object.values(SITES));

  // 检查是否为特定站点
  const isCurrentSite = (siteId) => {
    return currentSiteId.value === siteId;
  };

  // 检查是否为AWS站点
  const isAWSSite = computed(() => currentSiteId.value === 'aws');

  // 检查是否为GCP站点
  const isGCPSite = computed(() => currentSiteId.value === 'gcp');

  // 获取站点完整URL
  const getSiteUrl = (siteId) => {
    const site = SITES[siteId];
    if (!site) return null;
    return `${site.protocol}${site.url}`;
  };

  // 切换站点
  const switchToSite = async (siteId, options = {}) => {
    if (!SITES[siteId]) {
      throw new Error(`Unknown site: ${siteId}`);
    }

    if (isCurrentSite(siteId)) {
      console.log('Already on target site');
      return;
    }

    const site = SITES[siteId];
    const targetUrl = getSiteUrl(siteId);

    // 保存当前状态（可选）
    if (options.saveState !== false) {
      const currentState = {
        timestamp: Date.now(),
        currentUrl: window.location.href,
        currentSite: currentSiteId.value,
        userAgent: navigator.userAgent
      };
      localStorage.setItem('siteSwitchState', JSON.stringify(currentState));
    }

    // 设置切换状态
    isSwitching.value = true;

    try {
      // 如果有延迟选项，等待指定时间
      if (options.delay && options.delay > 0) {
        await new Promise(resolve => setTimeout(resolve, options.delay));
      }

      // 跳转到目标站点
      if (options.newTab) {
        window.open(targetUrl, '_blank');
      } else {
        window.location.href = targetUrl;
      }

    } catch (error) {
      console.error('Site switch failed:', error);
      isSwitching.value = false;
      throw error;
    }
  };

  // 获取站点切换历史状态
  const getSwitchState = () => {
    try {
      const state = localStorage.getItem('siteSwitchState');
      return state ? JSON.parse(state) : null;
    } catch (error) {
      console.error('Failed to parse switch state:', error);
      return null;
    }
  };

  // 清除站点切换状态
  const clearSwitchState = () => {
    localStorage.removeItem('siteSwitchState');
  };

  // 检查是否有待恢复的状态
  const hasPendingSwitch = () => {
    const state = getSwitchState();
    if (!state) return false;
    
    // 检查状态是否过期（5分钟）
    const isExpired = Date.now() - state.timestamp > 5 * 60 * 1000;
    return !isExpired;
  };

  // 恢复站点状态（如果需要）
  const restoreSwitchState = () => {
    const state = getSwitchState();
    if (!state) return false;

    // 检查状态是否过期
    const isExpired = Date.now() - state.timestamp > 5 * 60 * 1000;
    if (isExpired) {
      clearSwitchState();
      return false;
    }

    // 可以在这里添加恢复逻辑，比如恢复用户偏好设置等
    console.log('Restoring switch state:', state);
    return true;
  };

  // 获取站点显示名称（支持国际化）
  const getSiteDisplayName = (siteId, locale = 'en') => {
    const site = SITES[siteId];
    if (!site) return 'Unknown Site';

    // 这里可以根据locale返回不同的名称
    const displayNames = {
      en: {
        aws: 'AWS Manager',
        gcp: 'GCP Manager'
      },
      zh: {
        aws: 'AWS管理器',
        gcp: 'GCP管理器'
      },
      ja: {
        aws: 'AWSマネージャー',
        gcp: 'GCPマネージャー'
      }
    };

    return displayNames[locale]?.[siteId] || site.name;
  };

  // 获取站点图标
  const getSiteIcon = (siteId) => {
    return SITES[siteId]?.icon || 'fas fa-server';
  };

  // 获取站点提供商
  const getSiteProvider = (siteId) => {
    return SITES[siteId]?.provider || 'unknown';
  };

  return {
    // 状态
    currentSiteId: computed(() => currentSiteId.value),
    currentSite,
    availableSites,
    allSites,
    isSwitching: computed(() => isSwitching.value),
    
    // 检查函数
    isCurrentSite,
    isAWSSite,
    isGCPSite,
    
    // 操作函数
    switchToSite,
    getSiteUrl,
    getSiteDisplayName,
    getSiteIcon,
    getSiteProvider,
    
    // 状态管理
    getSwitchState,
    clearSwitchState,
    hasPendingSwitch,
    restoreSwitchState,
    
    // 工具函数
    detectCurrentSite
  };
}
